/**
 * Selenographic & Astronomical Mathematics Utility
 * Implements precision coordinate transformations and ephemeris calculations.
 */

export const MOON_RADIUS_KM = 1737.4;
export const SYNODIC_MONTH_DAYS = 29.530588853;

/**
 * Converts Selenographic Latitude & Longitude to 3D Cartesian coordinates.
 * @param {number} lat - Selenographic Latitude in degrees (-90 to +90)
 * @param {number} lon - Selenographic Longitude in degrees (-180 to +180)
 * @param {number} radius - Sphere radius in 3D world units
 * @returns {THREE.Vector3}
 */
export function latLonToVector3(lat, lon, radius = 5) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);

  const x = -(radius * Math.sin(phi) * Math.cos(theta));
  const z = radius * Math.sin(phi) * Math.sin(theta);
  const y = radius * Math.cos(phi);

  return { x, y, z };
}

/**
 * Converts 3D Cartesian coordinates on the Moon sphere back to Selenographic Lat/Lon.
 * @param {THREE.Vector3} point - Surface intersection point
 * @param {number} radius - Sphere radius
 * @returns {{lat: number, lon: number}}
 */
export function vector3ToLatLon(point, radius = 5) {
  const normX = point.x / radius;
  const normY = Math.max(-1, Math.min(1, point.y / radius));
  const normZ = point.z / radius;

  const lat = 90 - Math.acos(normY) * (180 / Math.PI);
  let lon = Math.atan2(normZ, -normX) * (180 / Math.PI) - 180;

  // Normalize lon to [-180, 180]
  while (lon < -180) lon += 360;
  while (lon > 180) lon -= 360;

  return { lat, lon };
}

/**
 * Format Latitude with N/S notation.
 * @param {number} lat
 * @returns {string}
 */
export function formatLat(lat) {
  const dir = lat >= 0 ? 'N' : 'S';
  return `${Math.abs(lat).toFixed(2)}° ${dir}`;
}

/**
 * Format Longitude with E/W notation.
 * @param {number} lon
 * @returns {string}
 */
export function formatLon(lon) {
  const dir = lon >= 0 ? 'E' : 'W';
  return `${Math.abs(lon).toFixed(2)}° ${dir}`;
}

/**
 * Calculate camera altitude above the lunar surface in kilometers.
 * @param {number} cameraDistance - Camera distance in 3D scene units
 * @param {number} sphereRadius - Moon sphere radius in 3D scene units
 * @returns {number} Altitude in kilometers
 */
export function calculateAltitudeKm(cameraDistance, sphereRadius = 5) {
  const ratio = (cameraDistance - sphereRadius) / sphereRadius;
  return Math.max(0, ratio * MOON_RADIUS_KM);
}
